import { create } from 'zustand'

type ActiveReference = {
  referenceId: string
}

type ExpandedReference = {
  messageId: string
  referenceId: string
}

type ReferencesPanelScrollTarget = {
  referenceId?: string
  messageId?: string
}

type ActiveMessageAndReferenceState = {
  activeMessageId?: string
  activeReference?: ActiveReference
  expandedReferences: ExpandedReference[]
  referencesPanelScrollContainers?: Record<string, HTMLDivElement>
  referencesPanelScrollTarget?: ReferencesPanelScrollTarget
}

type OnClickInlineReferenceInput = { isActiveReference: boolean, messageId: string, referenceId: string }

type ActiveMessageAndReferenceActions
  = {
    onClickInlineReference: ({ isActiveReference, messageId, referenceId }: OnClickInlineReferenceInput) => void
    onClickMessageSourcesButton: (messageId: string) => void
    onClickReferenceExpansionToggle: (messageId: string, referenceId: string) => void
    onResetActiveMessageAndReference: VoidFunction
    onSetReferencesPanelScrollContainer: (messageId: string, scrollContainer: HTMLDivElement) => void
  }

type ActiveMessageAndReferenceActionsStore = ActiveMessageAndReferenceState & ActiveMessageAndReferenceActions

export const useActiveMessageAndReferenceStore = create<ActiveMessageAndReferenceActionsStore>(set => ({
  activeMessageId: '',
  activeReference: undefined,
  expandedReferences: [],
  referencesPanelScrollContainers: {},
  referencesPanelScrollTarget: undefined,

  onClickInlineReference: ({
    isActiveReference,
    messageId,
    referenceId,
  }) => {
    set((state) => {
      const newExpandedReferences = isActiveReference
        ? [...state.expandedReferences].filter(expandedReference => expandedReference.referenceId !== referenceId)
        : [...state.expandedReferences, { messageId, referenceId }]

      return {
        activeMessageId: isActiveReference ? undefined : messageId,
        activeReference: isActiveReference ? undefined : { referenceId },
        referencesPanelScrollTarget: isActiveReference ? undefined : { messageId, referenceId },
        expandedReferences: newExpandedReferences,
      }
    })
  },

  onClickMessageSourcesButton: (messageId: string) => {
    set(() => ({
      activeMessageId: messageId,
      activeReference: undefined,
    }))
  },

  onClickReferenceExpansionToggle: (messageId: string, referenceId: string) => {
    set((state) => {
      const isExpanded = state.expandedReferences.some(
        ref => ref.messageId === messageId && ref.referenceId === referenceId,
      )

      return {
        expandedReferences: isExpanded
          ? state.expandedReferences.filter(
              ref => !(ref.messageId === messageId && ref.referenceId === referenceId),
            )
          : [...state.expandedReferences, { messageId, referenceId }],
      }
    })
  },

  onResetActiveMessageAndReference: () => {
    set(() => ({
      activeMessageId: undefined,
      activeReference: undefined,
    }),
    )
  },

  onSetReferencesPanelScrollContainer: (messageId, scrollContainer) =>
    set(state => ({
      referencesPanelScrollContainers: { ...state.referencesPanelScrollContainers, [messageId]: scrollContainer },
    })),
}))

export const useActiveMessageId = () => useActiveMessageAndReferenceStore(state => state.activeMessageId)

export const useActiveReference = () => useActiveMessageAndReferenceStore(state => state.activeReference)

export const useExpandedReferences = () => useActiveMessageAndReferenceStore(state => state.expandedReferences)

export const useOnClickInlineReference = () => useActiveMessageAndReferenceStore(state => state.onClickInlineReference)

export const useOnClickMessageSourcesButton = () => useActiveMessageAndReferenceStore(state => state.onClickMessageSourcesButton)

export const useOnClickReferenceExpansionToggle = () => useActiveMessageAndReferenceStore(state => state.onClickReferenceExpansionToggle)

export const useOnResetActiveMessageAndReference = () => useActiveMessageAndReferenceStore(state => state.onResetActiveMessageAndReference)

export const useOnSetReferencesScrollContainer = () => useActiveMessageAndReferenceStore(state => state.onSetReferencesPanelScrollContainer)

export const useReferencesPanelScrollContainer = (messageId: string) => useActiveMessageAndReferenceStore(state => state.referencesPanelScrollContainers?.[messageId])

export const useReferencesPanelScrollTarget = () => useActiveMessageAndReferenceStore(state => state.referencesPanelScrollTarget)
